import { useState } from "react";
import axios from "axios";
import { Download, ExternalLink, FileText, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CHARSTAN_PR_RUN_005 — Procurement Pack Generator widget.
 *
 * Sits at the top of /admin/brand-assets. One click → mints inspector token,
 * assembles the controlled-doc inventory, renders co-branded PDF, returns
 * download link + public inspector URL.
 */
export default function ProcurementPackGenerator({ isPtBr }) {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const labels = isPtBr
    ? {
        title: "Gerador de Pacote de Procurement",
        subtitle:
          "Gere um pacote PDF cobrandado para um cliente em potencial — inclui inventário de documentos controlados, URL do portal do auditor e código QR.",
        clientName: "Nome do Cliente",
        clientEmail: "E-mail (opcional)",
        expires: "Expira em (dias)",
        generate: "Gerar Pacote",
        generating: "Gerando…",
        downloaded: "Baixar PDF",
        inspectorUrl: "URL do Inspetor (entregar ao auditor)",
        copy: "Copiar",
        copied: "Copiado!",
        documents: "documentos incluídos",
        chainOk: "Cadeia hash íntegra",
      }
    : {
        title: "Procurement Pack Generator",
        subtitle:
          "Generate a co-branded PDF pack for a prospective client — controlled-document inventory, auditor portal URL, and QR code in one handoff.",
        clientName: "Client Name",
        clientEmail: "Email (optional)",
        expires: "Expires in (days)",
        generate: "Generate Pack",
        generating: "Generating…",
        downloaded: "Download PDF",
        inspectorUrl: "Inspector URL (hand to auditor)",
        copy: "Copy",
        copied: "Copied!",
        documents: "documents included",
        chainOk: "Hash chain intact",
      };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!clientName || clientName.trim().length < 2) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const tenantId = clientName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60);
      const res = await axios.post(
        `${API}/dcm/procurement-pack`,
        {
          client_name: clientName.trim(),
          client_email: clientEmail.trim() || null,
          tenant_id: tenantId,
          expires_in_days: Number(expiresInDays) || 30,
          include_tiers: ["T1", "T2"],
          include_statuses: ["issued"],
        },
        { withCredentials: true }
      );
      setResult(res.data);
    } catch (err) {
      setError(
        err.response?.data?.detail
          ? typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : JSON.stringify(err.response.data.detail)
          : err.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (!result?.inspector_url) return;
    navigator.clipboard.writeText(result.inspector_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPdf = async () => {
    const res = await axios.get(`${API}/dcm/procurement-pack/${result.pack_id}/pdf`, {
      withCredentials: true,
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Charstan_Procurement_Pack_${clientName.replace(/\s+/g, "_")}_${result.pack_id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      className="bg-gradient-to-br from-[rgba(0,200,224,0.06)] to-[rgba(63,111,177,0.04)] border border-[rgba(0,200,224,0.18)] rounded-lg p-6"
      data-testid="procurement-pack-generator"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="p-3 bg-[rgba(0,200,224,0.12)] rounded-lg">
          <ShieldCheck className="w-6 h-6 text-[#00C8E0]" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl text-white font-light tracking-tight mb-1" data-testid="procurement-pack-title">
            {labels.title}
          </h2>
          <p className="text-sm text-[#94A3B8] max-w-2xl">{labels.subtitle}</p>
        </div>
      </div>

      <form onSubmit={handleGenerate} className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
        <div className="md:col-span-5">
          <label className="block text-[10px] uppercase tracking-widest text-[#64748B] mb-1.5">
            {labels.clientName}
          </label>
          <input
            type="text"
            required
            minLength={2}
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="ACME Pharma Inc."
            data-testid="procurement-client-name-input"
            className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white placeholder-[#64748B] focus:border-[#00C8E0] focus:outline-none"
          />
        </div>
        <div className="md:col-span-4">
          <label className="block text-[10px] uppercase tracking-widest text-[#64748B] mb-1.5">
            {labels.clientEmail}
          </label>
          <input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            placeholder="auditor@acmepharma.example"
            data-testid="procurement-client-email-input"
            className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white placeholder-[#64748B] focus:border-[#00C8E0] focus:outline-none"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-[#64748B] mb-1.5">
            {labels.expires}
          </label>
          <input
            type="number"
            min={1}
            max={365}
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value)}
            data-testid="procurement-expires-input"
            className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white focus:border-[#00C8E0] focus:outline-none"
          />
        </div>
        <div className="md:col-span-1 flex items-end">
          <button
            type="submit"
            disabled={submitting || !clientName.trim()}
            data-testid="procurement-generate-btn"
            className="w-full h-[38px] bg-[#00C8E0] hover:bg-[#00A8C0] disabled:bg-[rgba(255,255,255,0.06)] disabled:text-[#475569] text-[#0F172A] text-xs font-semibold uppercase tracking-wider rounded transition-colors flex items-center justify-center gap-1"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          </button>
        </div>
      </form>

      {error && (
        <div
          className="bg-[rgba(220,38,38,0.08)] border border-[rgba(220,38,38,0.3)] rounded p-3 text-sm text-[#FCA5A5] mb-3"
          data-testid="procurement-error"
        >
          {error}
        </div>
      )}

      {result && (
        <div
          className="bg-[rgba(34,197,94,0.06)] border border-[rgba(34,197,94,0.25)] rounded-lg p-4 space-y-4"
          data-testid="procurement-result"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-2 py-1 bg-[rgba(34,197,94,0.15)] text-[#86EFAC] text-[10px] font-mono rounded">
              {result.pack_id}
            </span>
            <span className="text-xs text-[#94A3B8]">
              <FileText className="w-3 h-3 inline mr-1" />
              {result.documents_count} {labels.documents}
            </span>
            {result.chain_ok && (
              <span className="text-xs text-[#86EFAC]">
                <ShieldCheck className="w-3 h-3 inline mr-1" />
                {labels.chainOk} (head_seq {result.chain_head_seq})
              </span>
            )}
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#64748B] mb-1.5">
              {labels.inspectorUrl}
            </label>
            <div className="flex items-stretch gap-2">
              <code
                className="flex-1 bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.06)] rounded px-3 py-2 text-xs text-[#00C8E0] font-mono break-all"
                data-testid="procurement-inspector-url"
              >
                {result.inspector_url}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                data-testid="procurement-copy-url-btn"
                className="px-3 py-2 bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.1)] rounded text-xs text-white"
              >
                {copied ? labels.copied : labels.copy}
              </button>
              <a
                href={result.inspector_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="procurement-open-url-btn"
                className="px-3 py-2 bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.1)] rounded text-xs text-white flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          <button
            type="button"
            onClick={downloadPdf}
            data-testid="procurement-download-pdf-btn"
            className="w-full h-[38px] bg-[#00C8E0] hover:bg-[#00A8C0] text-[#0F172A] text-xs font-semibold uppercase tracking-wider rounded transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            {labels.downloaded} ({Math.round(result.size_bytes / 1024)} KB)
          </button>
        </div>
      )}
    </section>
  );
}
